import React, { useState, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Spinner } from "reactstrap"
import { setBreadcrumbItems } from "../../../store/actions"
import { getSucursales, descargarExportCSV } from "./api"
import ResumenCards from "./ResumenCards"
import TablaPrefijos from "./TablaPrefijos"
import FiltrosFecha from "./FiltrosFecha"
import DrawerDetalle from "./DrawerDetalle"
import GraficoTopPrefijos from "./GraficoTopPrefijos"
import UltimaActualizacion from "./UltimaActualizacion"
import BotonExportGlobal from "./BotonExportGlobal"

const breadcrumbItems = [
  { title: "LS-DA", link: "#" },
  { title: "Comprobantes", link: "#" },
  { title: "Folios Faltantes", link: "#" },
]

const FoliosFaltantes = ({ empresaSeleccionada, user, setBreadcrumbItems: setBreadcrumb }) => {
  const [resumen, setResumen] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null)

  const [fechaDesde, setFechaDesde] = useState("")
  const [fechaHasta, setFechaHasta] = useState("")
  const [filtrosAplicados, setFiltrosAplicados] = useState({ desde: "", hasta: "" })

  const [prefijoActivo, setPrefijoActivo] = useState(null)
  const [drawerAbierto, setDrawerAbierto] = useState(false)
  const [exportingPrefijo, setExportingPrefijo] = useState("")

  const nitemisor = empresaSeleccionada?.nit || empresaSeleccionada?.identificacion || ""
  const empresaNombre = empresaSeleccionada?.nombre || ""

  useEffect(() => {
    document.title = "Folios Faltantes | LS-DA"
    const greeting = user
      ? `Bienvenido, ${user.nombre || ""} ${user.apellido || ""}`.trim()
      : "Folios Faltantes"
    setBreadcrumb(greeting, breadcrumbItems)
  }, [user, setBreadcrumb])

  const fechasAplicadas = Boolean(filtrosAplicados.desde && filtrosAplicados.hasta)

  const cargarResumen = useCallback(async () => {
    if (!nitemisor) return
    if (!filtrosAplicados.desde || !filtrosAplicados.hasta) return
    setLoading(true)
    setError("")
    try {
      const { data } = await getSucursales({
        nitemisor,
        fecha_desde: filtrosAplicados.desde,
        fecha_hasta: filtrosAplicados.hasta,
      })
      setResumen(data)
      setUltimaActualizacion(Date.now())
    } catch (err) {
      setError(err.response?.data?.message || "No se pudo cargar el resumen.")
      setResumen(null)
    } finally {
      setLoading(false)
    }
  }, [nitemisor, filtrosAplicados])

  // Resetear estado cuando cambia la empresa
  useEffect(() => {
    setResumen(null)
    setUltimaActualizacion(null)
    setError("")
    setFechaDesde("")
    setFechaHasta("")
    setFiltrosAplicados({ desde: "", hasta: "" })
  }, [nitemisor])

  // Cargar sólo cuando hay empresa + ambas fechas aplicadas
  useEffect(() => {
    cargarResumen()
  }, [cargarResumen])

  const handleAplicarFiltros = () => {
    setFiltrosAplicados({ desde: fechaDesde, hasta: fechaHasta })
  }

  const handleLimpiarFiltros = () => {
    setFechaDesde("")
    setFechaHasta("")
    setFiltrosAplicados({ desde: "", hasta: "" })
  }

  const handleVerDetalle = prefijo => {
    setPrefijoActivo(prefijo)
    setDrawerAbierto(true)
  }

  const handleCerrarDrawer = () => {
    setDrawerAbierto(false)
  }

  const handleDescargarCSV = async prefijo => {
    if (exportingPrefijo) return
    setExportingPrefijo(prefijo.prefijo)
    try {
      await descargarExportCSV({
        nitemisor,
        prefijo: prefijo.prefijo,
        fecha_desde: filtrosAplicados.desde,
        fecha_hasta: filtrosAplicados.hasta,
      })
    } catch (err) {
      setError(err.response?.data?.message || "No se pudo descargar el CSV.")
    } finally {
      setExportingPrefijo("")
    }
  }


  // ── Sin empresa ──
  if (!nitemisor) {
    return (
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: 60,
          textAlign: "center",
          border: "1px dashed #cbd5e1",
        }}
      >
        <i className="mdi mdi-domain-off" style={{ fontSize: 56, color: "#cbd5e1" }} />
        <h5 className="mt-3" style={{ color: "#0f172a" }}>Selecciona una empresa</h5>
        <p style={{ color: "#64748b", maxWidth: 420, margin: "8px auto 0" }}>
          Usa el selector <strong>“Seleccionar empresa”</strong> en la parte superior derecha
          para consultar los folios faltantes.
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* ── Header ── */}
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: "20px 24px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Comprobantes
            </div>
            <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0f172a" }}>
              Folios Faltantes
            </h3>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span>
                <i className="mdi mdi-domain me-1 text-primary" />
                <strong>{empresaNombre}</strong> · NIT {nitemisor}
              </span>
              {ultimaActualizacion && !loading && (
                <>
                  <span style={{ color: "#cbd5e1" }}>·</span>
                  <UltimaActualizacion timestamp={ultimaActualizacion} />
                </>
              )}
            </div>
          </div>
          <div className="d-flex align-items-end gap-2 flex-wrap">
            <FiltrosFecha
              fechaDesde={fechaDesde}
              fechaHasta={fechaHasta}
              onChange={({ fechaDesde: d, fechaHasta: h }) => {
                setFechaDesde(d)
                setFechaHasta(h)
              }}
              onAplicar={handleAplicarFiltros}
              onLimpiar={handleLimpiarFiltros}
              loading={loading}
            />
            {resumen && (
              <BotonExportGlobal
                nitemisor={nitemisor}
                prefijos={resumen.prefijos}
                fechaDesde={filtrosAplicados.desde}
                fechaHasta={filtrosAplicados.hasta}
              />
            )}
            {fechasAplicadas && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={cargarResumen}
                disabled={loading}
                title="Recargar"
              >
                <i className={`mdi mdi-refresh ${loading ? "mdi-spin" : ""}`} />
              </button>
            )}
          </div>
        </div>

        {fechasAplicadas && (
          <div
            style={{
              marginTop: 12,
              padding: "8px 14px",
              background: "#eff6ff",
              borderRadius: 8,
              fontSize: 13,
              color: "#1e40af",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <i className="mdi mdi-filter-variant" />
            Consultando rango: <strong>{filtrosAplicados.desde}</strong> →{" "}
            <strong>{filtrosAplicados.hasta}</strong>
          </div>
        )}
      </div>

      {/* ── Error ── */}
      {error && (
        <div
          style={{
            padding: "14px 18px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <i className="mdi mdi-alert-circle" style={{ fontSize: 20 }} />
          <span>{error}</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger ms-auto"
            onClick={cargarResumen}
          >
            Reintentar
          </button>
        </div>
      )}

      {/* ── Sin fechas aplicadas: prompt inicial ── */}
      {!fechasAplicadas && !loading && (
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            padding: "60px 40px",
            textAlign: "center",
            border: "1px dashed #cbd5e1",
          }}
        >
          <i className="mdi mdi-calendar-range" style={{ fontSize: 56, color: "#cbd5e1" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>
            Selecciona un rango de fechas
          </h5>
          <p style={{ color: "#64748b", maxWidth: 460, margin: "8px auto 0", fontSize: 14 }}>
            Indica la fecha <strong>desde</strong> y <strong>hasta</strong> en la barra superior y
            pulsa <strong>Consultar</strong> para ver los folios faltantes de{" "}
            <strong>{empresaNombre}</strong>.
          </p>
          <p style={{ color: "#94a3b8", marginTop: 12, fontSize: 12 }}>
            <i className="mdi mdi-information-outline me-1" />
            Acotar el rango evita cargas innecesarias y acelera la consulta.
          </p>
        </div>
      )}

      {/* ── Loading ── */}
      {fechasAplicadas && loading && !resumen && (
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            padding: 80,
            textAlign: "center",
            color: "#64748b",
            border: "1px solid #e2e8f0",
          }}
        >
          <Spinner color="primary" />
          <div className="mt-2">Cargando resumen…</div>
        </div>
      )}

      {/* ── Resumen + gráfico + tabla ── */}
      {fechasAplicadas && resumen && (
        <>
          <ResumenCards resumen={resumen} />
          <GraficoTopPrefijos prefijos={resumen.prefijos} />
          <div
            style={{
              background: "#fff",
              borderRadius: 14,
              padding: 20,
              border: "1px solid #e2e8f0",
            }}
          >
            <TablaPrefijos
              prefijos={resumen.prefijos}
              sucursalesDisponibles={(resumen.sucursales || []).map(s => s.sucursal)}
              loading={false}
              onVerDetalle={handleVerDetalle}
              onDescargarCSV={handleDescargarCSV}
              exportingPrefijo={exportingPrefijo}
            />
          </div>
        </>
      )}

      {/* ── Drawer detalle ── */}
      <DrawerDetalle
        abierto={drawerAbierto}
        onCerrar={handleCerrarDrawer}
        prefijo={prefijoActivo}
        nitemisor={nitemisor}
        empresaNombre={empresaNombre}
        fechaDesde={filtrosAplicados.desde}
        fechaHasta={filtrosAplicados.hasta}
        onDescargarCSV={handleDescargarCSV}
        exportingPrefijo={exportingPrefijo}
      />
    </div>
  )
}

FoliosFaltantes.propTypes = {
  empresaSeleccionada: PropTypes.object,
  user: PropTypes.object,
  setBreadcrumbItems: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  empresaSeleccionada: state.Login.empresaSeleccionada,
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(FoliosFaltantes)
